import ProtectedRoute from "@/components/ProtectedRoute";
import RoleGate from "@/components/RoleGate";
import Lucide from "@/components/Base/Lucide";
import { FormSelect } from "@/components/Base/Form";
import Litepicker from "@/components/Base/Litepicker";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { getRole, getToken } from "@/utils/auth";
import Table from "@/components/Base/Table";

type StockBy = "Region" | "Class" | "Outlet";

type OutletGroup = {
    id: string;
    groupName: string;
};

type Region = {
    id: string;
    name: string;
};

type OutletCategory = {
    id: string;
    catName: string;
};

type StockOutletGroupTable = {
    success?: boolean;
    date: string;
    outletGroup: { id: string; groupName: string };
    columns: Array<{ productId: string; productName: string }>;
    rows: Array<{
        outletId: string;
        outletName: string;
        outletCategoryId: string | null;
        openings: Array<number | null>;
        closings: Array<number | null>;
        openingsBelowThreshold: boolean[];
        closingsBelowThreshold: boolean[];
    }>;
};

type StockRegionTable = {
    success?: boolean;
    date: string;
    region: { id: string; name: string };
    columns: Array<{ productId: string; productName: string }>;
    rows: Array<{
        outletId: string;
        outletName: string;
        outletCategoryId: string | null;
        openings: Array<number | null>;
        closings: Array<number | null>;
        openingsBelowThreshold: boolean[];
        closingsBelowThreshold: boolean[];
    }>;
};

type StockOutletCategoryTable = {
    success?: boolean;
    date: string;
    outletCategory: { id: string; catName: string };
    columns: Array<{ productId: string; productName: string }>;
    rows: Array<{
        outletId: string;
        outletName: string;
        outletCategoryId: string | null;
        openings: Array<number | null>;
        closings: Array<number | null>;
        openingsBelowThreshold: boolean[];
        closingsBelowThreshold: boolean[];
    }>;
};

const formatIntCell = (value: number | null) => {
    if (value === null || value === undefined) return "-";
    return Math.round(value).toLocaleString("en-US");
};

export default function Stock() {
    const { projectId } = useParams<{ projectId: string }>();
    const role = useMemo(() => getRole(), []);
    const apiUrl = useMemo(
        () => import.meta.env.VITE_API_URL || "http://localhost:4000",
        []
    );

    const [stockBy, setStockBy] = useState<StockBy>("Outlet");
    const [date, setDate] = useState(() => dayjs().format("YYYY-MM-DD"));
    const [companyId, setCompanyId] = useState<string | null>(null);

    const [loadingOutletGroups, setLoadingOutletGroups] = useState(false);
    const [outletGroups, setOutletGroups] = useState<OutletGroup[]>([]);
    const [expandedOutletGroupIds, setExpandedOutletGroupIds] = useState<Set<string>>(
        () => new Set()
    );
    const [expandPrefsLoaded, setExpandPrefsLoaded] = useState(false);
    const [savedKnownOutletGroupIds, setSavedKnownOutletGroupIds] = useState<string[] | null>(
        null
    );

    const [loadingRegions, setLoadingRegions] = useState(false);
    const [regions, setRegions] = useState<Region[]>([]);
    const [expandedRegionIds, setExpandedRegionIds] = useState<Set<string>>(() => new Set());
    const [regionExpandPrefsLoaded, setRegionExpandPrefsLoaded] = useState(false);
    const [savedKnownRegionIds, setSavedKnownRegionIds] = useState<string[] | null>(null);

    const [loadingClasses, setLoadingClasses] = useState(false);
    const [classes, setClasses] = useState<OutletCategory[]>([]);
    const [expandedClassIds, setExpandedClassIds] = useState<Set<string>>(() => new Set());
    const [classExpandPrefsLoaded, setClassExpandPrefsLoaded] = useState(false);
    const [savedKnownClassIds, setSavedKnownClassIds] = useState<string[] | null>(null);

    const [loadingTableByOutletGroupId, setLoadingTableByOutletGroupId] = useState<
        Record<string, boolean>
    >({});
    const [tableErrorByOutletGroupId, setTableErrorByOutletGroupId] = useState<
        Record<string, string>
    >({});
    const [tableByOutletGroupId, setTableByOutletGroupId] = useState<
        Record<string, StockOutletGroupTable>
    >({});

    const [loadingTableByRegionId, setLoadingTableByRegionId] = useState<Record<string, boolean>>(
        {}
    );
    const [tableErrorByRegionId, setTableErrorByRegionId] = useState<Record<string, string>>({});
    const [tableByRegionId, setTableByRegionId] = useState<Record<string, StockRegionTable>>({});

    const [loadingTableByClassId, setLoadingTableByClassId] = useState<Record<string, boolean>>(
        {}
    );
    const [tableErrorByClassId, setTableErrorByClassId] = useState<Record<string, string>>({});
    const [tableByClassId, setTableByClassId] = useState<Record<string, StockOutletCategoryTable>>(
        {}
    );

    // Use refs to track which IDs are currently being fetched (prevents infinite loops)
    const fetchingOutletGroupRef = useRef<Set<string>>(new Set());
    const fetchingRegionRef = useRef<Set<string>>(new Set());
    const fetchingClassRef = useRef<Set<string>>(new Set());

    const outletGroupExpandStorageKey = useMemo(() => {
        return projectId ? `stock_outletgroups_expanded_${projectId}` : null;
    }, [projectId]);

    const regionExpandStorageKey = useMemo(() => {
        return projectId ? `stock_regions_expanded_${projectId}` : null;
    }, [projectId]);

    const classExpandStorageKey = useMemo(() => {
        return projectId ? `stock_classes_expanded_${projectId}` : null;
    }, [projectId]);

    const viewPrefsStorageKey = useMemo(() => {
        return projectId ? `stock_view_prefs_${projectId}` : null;
    }, [projectId]);

    // Restore Stock By + Date (per project) - same behavior as Pricing.tsx
    useEffect(() => {
        if (!viewPrefsStorageKey) return;
        try {
            const raw = localStorage.getItem(viewPrefsStorageKey);
            if (!raw) return;
            const parsed = JSON.parse(raw);
            if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return;

            const sb = (parsed as any).stockBy;
            if (sb === "Region" || sb === "Class" || sb === "Outlet") {
                setStockBy(sb);
            }
            const d = (parsed as any).date;
            if (typeof d === "string" && d.trim()) {
                setDate(d);
            }
        } catch {
            // ignore
        }
    }, [viewPrefsStorageKey]);

    // Persist Stock By + Date (per project)
    useEffect(() => {
        if (!viewPrefsStorageKey) return;
        try {
            localStorage.setItem(
                viewPrefsStorageKey,
                JSON.stringify({
                    v: 1,
                    stockBy,
                    date,
                })
            );
        } catch {
            // ignore
        }
    }, [viewPrefsStorageKey, stockBy, date]);

    useEffect(() => {
        let cancelled = false;

        const fetchProjectCompanyId = async () => {
            if (!projectId) return null;
            const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:4000";
            const res = await fetch(`${apiUrl}/projects/${projectId}`, {
                headers: { Authorization: `Bearer ${getToken()}` },
            });
            if (!res.ok) return null;
            const json = await res.json();
            return json?.data?.companyId || null;
        };

        (async () => {
            if (role !== "SUPERADMIN") return;
            const cid = await fetchProjectCompanyId();
            if (!cancelled) setCompanyId(cid);
        })();

        return () => {
            cancelled = true;
        };
    }, [projectId, role]);

    // Load Regions for Stock By: Region (backend-scoped by projectId)
    useEffect(() => {
        if (stockBy !== "Region") return;
        if (!projectId) return;

        const controller = new AbortController();

        (async () => {
            try {
                setLoadingRegions(true);
                const params = new URLSearchParams();
                params.append("projectId", projectId);

                const res = await fetch(`${apiUrl}/stock/regions?${params.toString()}`, {
                    signal: controller.signal,
                    headers: { Authorization: `Bearer ${getToken()}` },
                });
                const json = await res.json().catch(() => ({}));
                if (!res.ok) {
                    throw new Error(
                        (json as any)?.error ||
                            (json as any)?.message ||
                            "Failed to fetch regions"
                    );
                }
                const data = Array.isArray((json as any)?.data) ? (json as any).data : [];
                setRegions(
                    data.map((r: any) => ({
                        id: r.id,
                        name: r.name,
                    }))
                );
            } catch (e: any) {
                if (e?.name === "AbortError") return;
                console.error(e);
                setRegions([]);
            } finally {
                setLoadingRegions(false);
            }
        })();

        return () => controller.abort();
    }, [apiUrl, projectId, stockBy]);

    // Load Classes (Outlet Categories) for Stock By: Class
    useEffect(() => {
        if (stockBy !== "Class") return;
        if (!projectId) return;

        const controller = new AbortController();

        (async () => {
            try {
                setLoadingClasses(true);
                const params = new URLSearchParams();
                params.append("projectId", projectId);

                const res = await fetch(
                    `${apiUrl}/stock/outlet-categories?${params.toString()}`,
                    {
                        signal: controller.signal,
                        headers: { Authorization: `Bearer ${getToken()}` },
                    }
                );
                const json = await res.json().catch(() => ({}));
                if (!res.ok) {
                    throw new Error(
                        (json as any)?.error ||
                            (json as any)?.message ||
                            "Failed to fetch outlet categories"
                    );
                }
                const data = Array.isArray((json as any)?.data) ? (json as any).data : [];
                setClasses(
                    data.map((c: any) => ({
                        id: c.id,
                        catName: c.catName,
                    }))
                );
            } catch (e: any) {
                if (e?.name === "AbortError") return;
                console.error(e);
                setClasses([]);
            } finally {
                setLoadingClasses(false);
            }
        })();

        return () => controller.abort();
    }, [apiUrl, projectId, stockBy]);

    useEffect(() => {
        let cancelled = false;

        const fetchAllOutletGroups = async () => {
            const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:4000";
            // Match Pricing page ordering (groupName asc)
            const pageSize = 100;
            let page = 1;
            let totalPages = 1;
            const all: OutletGroup[] = [];

            while (page <= totalPages) {
                const params = new URLSearchParams();
                params.set("page", String(page));
                params.set("pageSize", String(pageSize));
                params.set("sortBy", "groupName");
                params.set("sortOrder", "asc");
                if (role === "SUPERADMIN" && companyId) params.set("companyId", companyId);

                const res = await fetch(`${apiUrl}/outlet-groups?${params.toString()}`, {
                    headers: { Authorization: `Bearer ${getToken()}` },
                });
                if (!res.ok) throw new Error("Failed to fetch outlet groups");
                const json = await res.json();
                const data = Array.isArray(json?.data) ? (json.data as OutletGroup[]) : [];
                all.push(...data);
                totalPages = Number(json?.totalPages || 1);
                page += 1;
            }

            return all;
        };

        (async () => {
            try {
                if (stockBy !== "Outlet") return;
                if (role === "SUPERADMIN" && !companyId) return;

                setLoadingOutletGroups(true);
                const groups = await fetchAllOutletGroups();
                if (!cancelled) setOutletGroups(groups);
            } catch (e) {
                console.error(e);
                if (!cancelled) setOutletGroups([]);
            } finally {
                if (!cancelled) setLoadingOutletGroups(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [companyId, role, stockBy]);

    // Load aggregated outlet-group table data from backend (Pricing-style aggregation)
    useEffect(() => {
        if (stockBy !== "Outlet") return;
        if (!projectId) return;
        if (!date || !date.trim()) return;
        if (outletGroups.length === 0) return;

        const expandedIds = new Set(Array.from(expandedOutletGroupIds));
        const idsToFetch = outletGroups
            .map((g) => g.id)
            .filter((id) => expandedIds.has(id))
            .filter((id) => {
                // Check if already fetching
                if (fetchingOutletGroupRef.current.has(id)) return false;
                // Check if already loaded for this date
                const existing = tableByOutletGroupId[id];
                if (existing && existing.date === date) return false;
                return true;
            });

        if (idsToFetch.length === 0) return;

        let cancelled = false;
        const controllers: AbortController[] = [];

        (async () => {
            for (const outletGroupId of idsToFetch) {
                if (cancelled) return;
                
                // Mark as fetching
                fetchingOutletGroupRef.current.add(outletGroupId);
                setLoadingTableByOutletGroupId((prev) => ({
                    ...prev,
                    [outletGroupId]: true,
                }));
                setTableErrorByOutletGroupId((prev) => {
                    const next = { ...prev };
                    delete next[outletGroupId];
                    return next;
                });

                const controller = new AbortController();
                controllers.push(controller);

                try {
                    const params = new URLSearchParams();
                    params.append("projectId", projectId);
                    params.append("date", date);

                    const res = await fetch(
                        `${apiUrl}/stock/outlet-groups/${outletGroupId}/table?${params.toString()}`,
                        {
                            signal: controller.signal,
                            headers: { Authorization: `Bearer ${getToken()}` },
                        }
                    );
                    const json = await res.json().catch(() => ({}));
                    if (!res.ok) {
                        throw new Error(
                            (json as any)?.error ||
                                (json as any)?.message ||
                                "Failed to load stock table"
                        );
                    }
                    if (cancelled) return;

                    setTableByOutletGroupId((prev) => ({
                        ...prev,
                        [outletGroupId]: json as StockOutletGroupTable,
                    }));
                } catch (e: any) {
                    if (e?.name === "AbortError") return;
                    console.error(e);
                    if (cancelled) return;
                    setTableErrorByOutletGroupId((prev) => ({
                        ...prev,
                        [outletGroupId]: e?.message || "Failed to load stock table",
                    }));
                } finally {
                    if (cancelled) return;
                    // Remove from fetching ref
                    fetchingOutletGroupRef.current.delete(outletGroupId);
                    setLoadingTableByOutletGroupId((prev) => ({
                        ...prev,
                        [outletGroupId]: false,
                    }));
                }
            }
        })();

        return () => {
            cancelled = true;
            controllers.forEach((c) => {
                c.abort();
                // Clean up fetching refs
                idsToFetch.forEach((id) => fetchingOutletGroupRef.current.delete(id));
            });
        };
    }, [apiUrl, date, expandedOutletGroupIds, outletGroups, projectId, stockBy]); // Removed tableByOutletGroupId

    // Load aggregated region table data from backend
    useEffect(() => {
        if (stockBy !== "Region") return;
        if (!projectId) return;
        if (!date || !date.trim()) return;
        if (regions.length === 0) return;

        const expandedIds = new Set(Array.from(expandedRegionIds));
        const idsToFetch = regions
            .map((r) => r.id)
            .filter((id) => expandedIds.has(id))
            .filter((id) => {
                // Check if already fetching
                if (fetchingRegionRef.current.has(id)) return false;
                // Check if already loaded for this date
                const existing = tableByRegionId[id];
                if (existing && existing.date === date) return false;
                return true;
            });

        if (idsToFetch.length === 0) return;

        let cancelled = false;
        const controllers: AbortController[] = [];

        (async () => {
            for (const regionId of idsToFetch) {
                if (cancelled) return;
                
                // Mark as fetching
                fetchingRegionRef.current.add(regionId);
                setLoadingTableByRegionId((prev) => ({ ...prev, [regionId]: true }));
                setTableErrorByRegionId((prev) => {
                    const next = { ...prev };
                    delete next[regionId];
                    return next;
                });

                const controller = new AbortController();
                controllers.push(controller);

                try {
                    const params = new URLSearchParams();
                    params.append("projectId", projectId);
                    params.append("date", date);

                    const res = await fetch(
                        `${apiUrl}/stock/regions/${regionId}/table?${params.toString()}`,
                        {
                            signal: controller.signal,
                            headers: { Authorization: `Bearer ${getToken()}` },
                        }
                    );
                    const json = await res.json().catch(() => ({}));
                    if (!res.ok) {
                        throw new Error(
                            (json as any)?.error ||
                                (json as any)?.message ||
                                "Failed to load region stock table"
                        );
                    }
                    if (cancelled) return;
                    setTableByRegionId((prev) => ({
                        ...prev,
                        [regionId]: json as StockRegionTable,
                    }));
                } catch (e: any) {
                    if (e?.name === "AbortError") return;
                    console.error(e);
                    if (cancelled) return;
                    setTableErrorByRegionId((prev) => ({
                        ...prev,
                        [regionId]: e?.message || "Failed to load region stock table",
                    }));
                } finally {
                    if (cancelled) return;
                    // Remove from fetching ref
                    fetchingRegionRef.current.delete(regionId);
                    setLoadingTableByRegionId((prev) => ({ ...prev, [regionId]: false }));
                }
            }
        })();

        return () => {
            cancelled = true;
            controllers.forEach((c) => {
                c.abort();
                // Clean up fetching refs
                idsToFetch.forEach((id) => fetchingRegionRef.current.delete(id));
            });
        };
    }, [apiUrl, date, expandedRegionIds, projectId, regions, stockBy]); // Removed tableByRegionId

    // Load aggregated class (outlet category) table data from backend
    useEffect(() => {
        if (stockBy !== "Class") return;
        if (!projectId) return;
        if (!date || !date.trim()) return;
        if (classes.length === 0) return;

        const expandedIds = new Set(Array.from(expandedClassIds));
        const idsToFetch = classes
            .map((c) => c.id)
            .filter((id) => expandedIds.has(id))
            .filter((id) => {
                // Check if already fetching
                if (fetchingClassRef.current.has(id)) return false;
                // Check if already loaded for this date
                const existing = tableByClassId[id];
                if (existing && existing.date === date) return false;
                return true;
            });

        if (idsToFetch.length === 0) return;

        let cancelled = false;
        const controllers: AbortController[] = [];

        (async () => {
            for (const outletCategoryId of idsToFetch) {
                if (cancelled) return;
                
                // Mark as fetching
                fetchingClassRef.current.add(outletCategoryId);
                setLoadingTableByClassId((prev) => ({ ...prev, [outletCategoryId]: true }));
                setTableErrorByClassId((prev) => {
                    const next = { ...prev };
                    delete next[outletCategoryId];
                    return next;
                });

                const controller = new AbortController();
                controllers.push(controller);

                try {
                    const params = new URLSearchParams();
                    params.append("projectId", projectId);
                    params.append("date", date);

                    const res = await fetch(
                        `${apiUrl}/stock/outlet-categories/${outletCategoryId}/table?${params.toString()}`,
                        {
                            signal: controller.signal,
                            headers: { Authorization: `Bearer ${getToken()}` },
                        }
                    );
                    const json = await res.json().catch(() => ({}));
                    if (!res.ok) {
                        throw new Error(
                            (json as any)?.error ||
                                (json as any)?.message ||
                                "Failed to load class stock table"
                        );
                    }
                    if (cancelled) return;
                    setTableByClassId((prev) => ({
                        ...prev,
                        [outletCategoryId]: json as StockOutletCategoryTable,
                    }));
                } catch (e: any) {
                    if (e?.name === "AbortError") return;
                    console.error(e);
                    if (cancelled) return;
                    setTableErrorByClassId((prev) => ({
                        ...prev,
                        [outletCategoryId]: e?.message || "Failed to load class stock table",
                    }));
                } finally {
                    if (cancelled) return;
                    // Remove from fetching ref
                    fetchingClassRef.current.delete(outletCategoryId);
                    setLoadingTableByClassId((prev) => ({ ...prev, [outletCategoryId]: false }));
                }
            }
        })();

        return () => {
            cancelled = true;
            controllers.forEach((c) => {
                c.abort();
                // Clean up fetching refs
                idsToFetch.forEach((id) => fetchingClassRef.current.delete(id));
            });
        };
    }, [apiUrl, classes, date, expandedClassIds, projectId, stockBy]); // Removed tableByClassId

    // Clear cached table data when date changes
    useEffect(() => {
        setTableByOutletGroupId({});
        setTableByRegionId({});
        setTableByClassId({});
        fetchingOutletGroupRef.current.clear();
        fetchingRegionRef.current.clear();
        fetchingClassRef.current.clear();
    }, [date]);

    // Restore expand/collapse state from localStorage (per project) - same logic as Pricing.tsx
    useEffect(() => {
        if (!outletGroupExpandStorageKey) return;
        try {
            const raw = localStorage.getItem(outletGroupExpandStorageKey);
            if (!raw) {
                setSavedKnownOutletGroupIds(null);
                setExpandPrefsLoaded(true);
                return;
            }

            const parsed = JSON.parse(raw);
            // New format: { v: 1, expanded: string[], known: string[] }
            if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
                const expanded = Array.isArray((parsed as any).expanded)
                    ? (parsed as any).expanded.filter((x: any) => typeof x === "string")
                    : [];
                const known = Array.isArray((parsed as any).known)
                    ? (parsed as any).known.filter((x: any) => typeof x === "string")
                    : null;

                setExpandedOutletGroupIds(new Set(expanded));
                setSavedKnownOutletGroupIds(known);
                setExpandPrefsLoaded(true);
                return;
            }

            // Legacy format: string[]
            if (Array.isArray(parsed)) {
                setExpandedOutletGroupIds(new Set(parsed.filter((x) => typeof x === "string")));
                setSavedKnownOutletGroupIds(null);
                setExpandPrefsLoaded(true);
            }
        } catch {
            setSavedKnownOutletGroupIds(null);
            setExpandPrefsLoaded(true);
        }
    }, [outletGroupExpandStorageKey]);

    // Restore expand/collapse state for Regions (per project)
    useEffect(() => {
        if (!regionExpandStorageKey) return;
        try {
            const raw = localStorage.getItem(regionExpandStorageKey);
            if (!raw) {
                setSavedKnownRegionIds(null);
                setRegionExpandPrefsLoaded(true);
                return;
            }

            const parsed = JSON.parse(raw);
            if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
                const expanded = Array.isArray((parsed as any).expanded)
                    ? (parsed as any).expanded.filter((x: any) => typeof x === "string")
                    : [];
                const known = Array.isArray((parsed as any).known)
                    ? (parsed as any).known.filter((x: any) => typeof x === "string")
                    : null;

                setExpandedRegionIds(new Set(expanded));
                setSavedKnownRegionIds(known);
                setRegionExpandPrefsLoaded(true);
                return;
            }

            if (Array.isArray(parsed)) {
                setExpandedRegionIds(new Set(parsed.filter((x) => typeof x === "string")));
                setSavedKnownRegionIds(null);
                setRegionExpandPrefsLoaded(true);
            }
        } catch {
            setSavedKnownRegionIds(null);
            setRegionExpandPrefsLoaded(true);
        }
    }, [regionExpandStorageKey]);

    // Restore expand/collapse state for Classes (per project)
    useEffect(() => {
        if (!classExpandStorageKey) return;
        try {
            const raw = localStorage.getItem(classExpandStorageKey);
            if (!raw) {
                setSavedKnownClassIds(null);
                setClassExpandPrefsLoaded(true);
                return;
            }

            const parsed = JSON.parse(raw);
            if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
                const expanded = Array.isArray((parsed as any).expanded)
                    ? (parsed as any).expanded.filter((x: any) => typeof x === "string")
                    : [];
                const known = Array.isArray((parsed as any).known)
                    ? (parsed as any).known.filter((x: any) => typeof x === "string")
                    : null;

                setExpandedClassIds(new Set(expanded));
                setSavedKnownClassIds(known);
                setClassExpandPrefsLoaded(true);
                return;
            }

            if (Array.isArray(parsed)) {
                setExpandedClassIds(new Set(parsed.filter((x) => typeof x === "string")));
                setSavedKnownClassIds(null);
                setClassExpandPrefsLoaded(true);
            }
        } catch {
            setSavedKnownClassIds(null);
            setClassExpandPrefsLoaded(true);
        }
    }, [classExpandStorageKey]);

    // Default: expand all outlet groups once loaded; keep user toggles for existing IDs and expand only truly new groups
    useEffect(() => {
        if (!expandPrefsLoaded) return;
        if (outletGroups.length === 0) return;
        setExpandedOutletGroupIds((prev) => {
            const next = new Set(prev);
            const currentIds = new Set(outletGroups.map((g) => g.id));

            // Remove groups that no longer exist
            Array.from(next).forEach((id) => {
                if (!currentIds.has(id)) next.delete(id);
            });

            // If no saved state exists at all, default to expanded for all groups
            if (prev.size === 0 && savedKnownOutletGroupIds === null) {
                outletGroups.forEach((g) => next.add(g.id));
                return next;
            }

            // If we have a known snapshot, expand only truly new groups (not previously known)
            if (Array.isArray(savedKnownOutletGroupIds)) {
                const knownSet = new Set(savedKnownOutletGroupIds);
                outletGroups.forEach((g) => {
                    if (!knownSet.has(g.id)) next.add(g.id);
                });
            }

            return next;
        });
    }, [outletGroups, expandPrefsLoaded, savedKnownOutletGroupIds]);

    // Default: expand all regions once loaded; keep user toggles and expand only truly new regions
    useEffect(() => {
        if (!regionExpandPrefsLoaded) return;
        if (regions.length === 0) return;
        setExpandedRegionIds((prev) => {
            const next = new Set(prev);
            const currentIds = new Set(regions.map((r) => r.id));

            Array.from(next).forEach((id) => {
                if (!currentIds.has(id)) next.delete(id);
            });

            if (prev.size === 0 && savedKnownRegionIds === null) {
                regions.forEach((r) => next.add(r.id));
                return next;
            }

            if (Array.isArray(savedKnownRegionIds)) {
                const knownSet = new Set(savedKnownRegionIds);
                regions.forEach((r) => {
                    if (!knownSet.has(r.id)) next.add(r.id);
                });
            }

            return next;
        });
    }, [regions, regionExpandPrefsLoaded, savedKnownRegionIds]);

    // Default: expand all classes once loaded; keep user toggles and expand only truly new classes
    useEffect(() => {
        if (!classExpandPrefsLoaded) return;
        if (classes.length === 0) return;
        setExpandedClassIds((prev) => {
            const next = new Set(prev);
            const currentIds = new Set(classes.map((c) => c.id));

            Array.from(next).forEach((id) => {
                if (!currentIds.has(id)) next.delete(id);
            });

            if (prev.size === 0 && savedKnownClassIds === null) {
                classes.forEach((c) => next.add(c.id));
                return next;
            }

            if (Array.isArray(savedKnownClassIds)) {
                const knownSet = new Set(savedKnownClassIds);
                classes.forEach((c) => {
                    if (!knownSet.has(c.id)) next.add(c.id);
                });
            }

            return next;
        });
    }, [classes, classExpandPrefsLoaded, savedKnownClassIds]);

    const toggleOutletGroup = (groupId: string) => {
        setExpandedOutletGroupIds((prev) => {
            const next = new Set(prev);
            if (next.has(groupId)) next.delete(groupId);
            else next.add(groupId);
            return next;
        });
    };

    const toggleRegion = (regionId: string) => {
        setExpandedRegionIds((prev) => {
            const next = new Set(prev);
            if (next.has(regionId)) next.delete(regionId);
            else next.add(regionId);
            return next;
        });
    };

    const toggleClass = (classId: string) => {
        setExpandedClassIds((prev) => {
            const next = new Set(prev);
            if (next.has(classId)) next.delete(classId);
            else next.add(classId);
            return next;
        });
    };

    // Persist expand/collapse state (includes known snapshot)
    useEffect(() => {
        if (!outletGroupExpandStorageKey) return;
        try {
            localStorage.setItem(
                outletGroupExpandStorageKey,
                JSON.stringify({
                    v: 1,
                    expanded: Array.from(expandedOutletGroupIds),
                    known: outletGroups.map((g) => g.id),
                })
            );
        } catch {
            // ignore
        }
    }, [expandedOutletGroupIds, outletGroupExpandStorageKey, outletGroups]);

    // Persist expand/collapse state for Regions
    useEffect(() => {
        if (!regionExpandStorageKey) return;
        try {
            localStorage.setItem(
                regionExpandStorageKey,
                JSON.stringify({
                    v: 1,
                    expanded: Array.from(expandedRegionIds),
                    known: regions.map((r) => r.id),
                })
            );
        } catch {
            // ignore
        }
    }, [expandedRegionIds, regionExpandStorageKey, regions]);

    // Persist expand/collapse state for Classes
    useEffect(() => {
        if (!classExpandStorageKey) return;
        try {
            localStorage.setItem(
                classExpandStorageKey,
                JSON.stringify({
                    v: 1,
                    expanded: Array.from(expandedClassIds),
                    known: classes.map((c) => c.id),
                })
            );
        } catch {
            // ignore
        }
    }, [classExpandStorageKey, classes, expandedClassIds]);

    return (
        <ProtectedRoute>
            <RoleGate allow={["SUPERADMIN", "ADMIN"]}>
                <div
                    style={{
                        width: "100vw",
                        maxWidth: "100vw",
                        marginLeft: "calc(-50vw + 50%)",
                        marginRight: "calc(-50vw + 50%)",
                        paddingLeft: "10rem",
                        paddingRight: "10rem",
                    }}
                >
                    <div style={{ width: "100%", maxWidth: "100%", overflow: "visible" }}>
                        <div className="grid grid-cols-12 gap-y-10 gap-x-6">
                            <div className="col-span-12">
                                {/* Filters (positioned top-left under breadcrumb) */}
                                <div className="mt-1 flex flex-col gap-3 sm:flex-row sm:items-center">
                                    <div className="relative inline-flex">
                                        <Lucide
                                            icon="Filter"
                                            className="absolute group-[.mode--light]:!text-slate-200 inset-y-0 left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3]"
                                        />
                                        <FormSelect
                                            value={stockBy}
                                            onChange={(e) =>
                                                setStockBy(e.target.value as StockBy)
                                            }
                                            className="min-w-[180px] w-auto pl-9 pr-8 py-2 rounded-[0.5rem] group-[.mode--light]:!bg-white/[0.12] group-[.mode--light]:!text-slate-200 group-[.mode--light]:!border-transparent dark:group-[.mode--light]:!bg-darkmode-900/30 dark:!box text-left text-sm whitespace-nowrap"
                                        >
                                            <option value="Region">Stock By: Region</option>
                                            <option value="Class">Stock By: Class</option>
                                            <option value="Outlet">Stock By: Outlet</option>
                                        </FormSelect>
                                    </div>

                                    {/* Date (styled like Pricing) */}
                                    <div className="relative inline-flex">
                                        <Lucide
                                            icon="Calendar"
                                            className="absolute group-[.mode--light]:!text-slate-200 inset-y-0 left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3]"
                                        />
                                        <Litepicker
                                            value={date}
                                            onChange={(e) => setDate(e.target.value)}
                                            options={{
                                                autoApply: true,
                                                singleMode: true,
                                                showWeekNumbers: false,
                                                dropdowns: {
                                                    minYear: 2020,
                                                    maxYear: null,
                                                    months: true,
                                                    years: true,
                                                },
                                                format: "YYYY-MM-DD",
                                            }}
                                            className="w-fit min-w-[220px] max-w-[320px] pl-9 pr-3 py-2 rounded-[0.5rem] group-[.mode--light]:!bg-white/[0.12] group-[.mode--light]:!text-slate-200 group-[.mode--light]:!border-transparent dark:group-[.mode--light]:!bg-darkmode-900/30 dark:!box text-sm"
                                        />
                                    </div>
                                </div>

                                {/* Stock By: Outlet (containers are OutletGroup) */}
                                {stockBy === "Outlet" && (
                                    <div className="mt-6 grid grid-cols-12 gap-x-6 gap-y-6">
                                        {loadingOutletGroups ? (
                                            <div className="col-span-12 flex items-center justify-center py-10">
                                                <div className="text-center">
                                                    <Lucide
                                                        icon="Loader2"
                                                        className="w-7 h-7 animate-spin mx-auto mb-2"
                                                    />
                                                    <div>Loading outlet groups...</div>
                                                </div>
                                            </div>
                                        ) : (
                                            outletGroups.map((g) => (
                                                <div key={g.id} className="col-span-12">
                                                    <div className="p-5 box">
                                                        <div className="flex items-center gap-3">
                                                            <div className="text-base font-medium">
                                                                {g.groupName}
                                                            </div>
                                                            <button
                                                                type="button"
                                                                className="ml-auto inline-flex items-center text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                                                                onClick={() => toggleOutletGroup(g.id)}
                                                                aria-expanded={expandedOutletGroupIds.has(g.id)}
                                                                aria-controls={`outletgroup-${g.id}`}
                                                            >
                                                                <Lucide
                                                                    icon={
                                                                        expandedOutletGroupIds.has(g.id)
                                                                            ? "ChevronUp"
                                                                            : "ChevronDown"
                                                                    }
                                                                    className="w-5 h-5 stroke-[1.3]"
                                                                />
                                                            </button>
                                                        </div>
                                                        {expandedOutletGroupIds.has(g.id) && (
                                                            <div
                                                                id={`outletgroup-${g.id}`}
                                                                className="mt-4"
                                                            >
                                                                <div className="overflow-auto">
                                                                    <Table className="border-b border-slate-200/60 text-xs">
                                                                        {(() => {
                                                                            const table =
                                                                                tableByOutletGroupId[g.id] ||
                                                                                null;
                                                                            const columns =
                                                                                table?.columns || [];
                                                                            const rows = table?.rows || [];
                                                                            const isLoading =
                                                                                !!loadingTableByOutletGroupId[
                                                                                    g.id
                                                                                ];
                                                                            const err =
                                                                                tableErrorByOutletGroupId[
                                                                                    g.id
                                                                                ] || null;

                                                                            if (!date || !date.trim()) {
                                                                                return (
                                                                                    <Table.Tbody>
                                                                                        <Table.Tr>
                                                                                            <Table.Td
                                                                                                className="py-4 text-slate-500"
                                                                                                colSpan={
                                                                                                    1 +
                                                                                                    (columns.length ||
                                                                                                        0) *
                                                                                                        2
                                                                                                }
                                                                                            >
                                                                                                Please select a date.
                                                                                            </Table.Td>
                                                                                        </Table.Tr>
                                                                                    </Table.Tbody>
                                                                                );
                                                                            }

                                                                            if (isLoading) {
                                                                                return (
                                                                                    <Table.Tbody>
                                                                                        <Table.Tr>
                                                                                            <Table.Td
                                                                                                className="py-4 text-slate-500"
                                                                                                colSpan={
                                                                                                    1 +
                                                                                                    (columns.length ||
                                                                                                        0) *
                                                                                                        2
                                                                                                }
                                                                                            >
                                                                                                Loading...
                                                                                            </Table.Td>
                                                                                        </Table.Tr>
                                                                                    </Table.Tbody>
                                                                                );
                                                                            }

                                                                            if (err) {
                                                                                return (
                                                                                    <Table.Tbody>
                                                                                        <Table.Tr>
                                                                                            <Table.Td
                                                                                                className="py-4 text-slate-500"
                                                                                                colSpan={
                                                                                                    1 +
                                                                                                    (columns.length ||
                                                                                                        0) *
                                                                                                        2
                                                                                                }
                                                                                            >
                                                                                                {err}
                                                                                            </Table.Td>
                                                                                        </Table.Tr>
                                                                                    </Table.Tbody>
                                                                                );
                                                                            }

                                                                            if (!table) {
                                                                                return (
                                                                                    <Table.Tbody>
                                                                                        <Table.Tr>
                                                                                            <Table.Td
                                                                                                className="py-4 text-slate-500"
                                                                                                colSpan={1}
                                                                                            >
                                                                                                No stock data loaded.
                                                                                            </Table.Td>
                                                                                        </Table.Tr>
                                                                                    </Table.Tbody>
                                                                                );
                                                                            }

                                                                            if (rows.length === 0) {
                                                                                return (
                                                                                    <Table.Tbody>
                                                                                        <Table.Tr>
                                                                                            <Table.Td
                                                                                                className="py-4 text-slate-500"
                                                                                                colSpan={1}
                                                                                            >
                                                                                                No outlets found for this outlet group.
                                                                                            </Table.Td>
                                                                                        </Table.Tr>
                                                                                    </Table.Tbody>
                                                                                );
                                                                            }

                                                                            if (columns.length === 0) {
                                                                                return (
                                                                                    <Table.Tbody>
                                                                                        <Table.Tr>
                                                                                            <Table.Td
                                                                                                className="py-4 text-slate-500"
                                                                                                colSpan={1}
                                                                                            >
                                                                                                No own products found.
                                                                                            </Table.Td>
                                                                                        </Table.Tr>
                                                                                    </Table.Tbody>
                                                                                );
                                                                            }

                                                                            return (
                                                                                <>
                                                                                    <Table.Thead className="text-xs bg-white dark:bg-darkmode-600">
                                                                                        <Table.Tr>
                                                                                            <Table.Td
                                                                                                rowSpan={2}
                                                                                                className="py-2 font-medium bg-slate-50 dark:bg-darkmode-700 sticky left-0 z-30 border-r border-slate-200 dark:border-darkmode-400 min-w-[220px]"
                                                                                            >
                                                                                                Outlet
                                                                                            </Table.Td>
                                                                                            {columns.map((p) => (
                                                                                                <Table.Td
                                                                                                    key={`head_${g.id}_${p.productId}`}
                                                                                                    colSpan={2}
                                                                                                    className="py-2 px-2 font-medium text-center bg-slate-50/70 dark:bg-darkmode-700/30 min-w-[120px] max-w-[120px] whitespace-normal break-words leading-snug"
                                                                                                >
                                                                                                    {p.productName}
                                                                                                </Table.Td>
                                                                                            ))}
                                                                                        </Table.Tr>
                                                                                        <Table.Tr>
                                                                                            {columns.map((p) => (
                                                                                                <React.Fragment
                                                                                                    key={`sub_${g.id}_${p.productId}`}
                                                                                                >
                                                                                                    <Table.Td className="py-2 px-2 font-medium text-center bg-success/10 dark:bg-success/10 whitespace-nowrap min-w-[60px] w-[60px]">
                                                                                                        Opening
                                                                                                    </Table.Td>
                                                                                                    <Table.Td className="py-2 px-2 font-medium text-center bg-warning/10 dark:bg-warning/10 whitespace-nowrap min-w-[60px] w-[60px]">
                                                                                                        Closing
                                                                                                    </Table.Td>
                                                                                                </React.Fragment>
                                                                                            ))}
                                                                                        </Table.Tr>
                                                                                    </Table.Thead>
                                                                                    <Table.Tbody>
                                                                                        {rows.map((o) => (
                                                                                            <Table.Tr
                                                                                                key={`out_${g.id}_${o.outletId}`}
                                                                                                className="[&_td]:border-b [&_td]:border-slate-200/60"
                                                                                            >
                                                                                                <Table.Td className="py-2 bg-slate-50 dark:bg-darkmode-700 whitespace-nowrap sticky left-0 z-20 border-r border-slate-200 dark:border-darkmode-400 min-w-[220px] max-w-[280px] truncate">
                                                                                                    {o.outletName}
                                                                                                </Table.Td>
                                                                                                {columns.map((p, idx) => (
                                                                                                    <React.Fragment
                                                                                                        key={`cell_${o.outletId}_${p.productId}`}
                                                                                                    >
                                                                                                        <Table.Td
                                                                                                            className={[
                                                                                                                "py-2 px-2 text-center tabular-nums min-w-[60px] w-[60px]",
                                                                                                                o.openingsBelowThreshold?.[
                                                                                                                    idx
                                                                                                                ]
                                                                                                                    ? "bg-danger/15 dark:bg-danger/20 text-danger font-medium"
                                                                                                                    : "bg-success/10 dark:bg-success/10",
                                                                                                            ].join(
                                                                                                                " "
                                                                                                            )}
                                                                                                        >
                                                                                                            {formatIntCell(
                                                                                                                o.openings?.[
                                                                                                                    idx
                                                                                                                ] ??
                                                                                                                    null
                                                                                                            )}
                                                                                                        </Table.Td>
                                                                                                        <Table.Td
                                                                                                            className={[
                                                                                                                "py-2 px-2 text-center tabular-nums min-w-[60px] w-[60px]",
                                                                                                                o.closingsBelowThreshold?.[
                                                                                                                    idx
                                                                                                                ]
                                                                                                                    ? "bg-danger/15 dark:bg-danger/20 text-danger font-medium"
                                                                                                                    : "bg-warning/10 dark:bg-warning/10",
                                                                                                            ].join(
                                                                                                                " "
                                                                                                            )}
                                                                                                        >
                                                                                                            {formatIntCell(
                                                                                                                o.closings?.[
                                                                                                                    idx
                                                                                                                ] ??
                                                                                                                    null
                                                                                                            )}
                                                                                                        </Table.Td>
                                                                                                    </React.Fragment>
                                                                                                ))}
                                                                                            </Table.Tr>
                                                                                        ))}
                                                                                    </Table.Tbody>
                                                                                </>
                                                                            );
                                                                        })()}
                                                                    </Table>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                )}

                                {/* Stock By: Region (containers are Region) */}
                                {stockBy === "Region" && (
                                    <div className="mt-6 grid grid-cols-12 gap-x-6 gap-y-6">
                                        {loadingRegions ? (
                                            <div className="col-span-12 flex items-center justify-center py-10">
                                                <div className="text-center">
                                                    <Lucide
                                                        icon="Loader2"
                                                        className="w-7 h-7 animate-spin mx-auto mb-2"
                                                    />
                                                    <div>Loading regions...</div>
                                                </div>
                                            </div>
                                        ) : regions.length === 0 ? (
                                            <div className="col-span-12 text-slate-500">
                                                No regions found for this project.
                                            </div>
                                        ) : (
                                            regions.map((r) => (
                                                <div key={r.id} className="col-span-12">
                                                    <div className="p-5 box">
                                                        <div className="flex items-center gap-3">
                                                            <div className="text-base font-medium">
                                                                {r.name}
                                                            </div>
                                                            <button
                                                                type="button"
                                                                className="ml-auto inline-flex items-center text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                                                                onClick={() => toggleRegion(r.id)}
                                                                aria-expanded={expandedRegionIds.has(r.id)}
                                                                aria-controls={`region-${r.id}`}
                                                            >
                                                                <Lucide
                                                                    icon={
                                                                        expandedRegionIds.has(r.id)
                                                                            ? "ChevronUp"
                                                                            : "ChevronDown"
                                                                    }
                                                                    className="w-5 h-5 stroke-[1.3]"
                                                                />
                                                            </button>
                                                        </div>

                                                        {expandedRegionIds.has(r.id) && (
                                                            <div
                                                                id={`region-${r.id}`}
                                                                className="mt-4"
                                                            >
                                                                <div className="overflow-auto">
                                                                    <Table className="border-b border-slate-200/60 text-xs">
                                                                        {(() => {
                                                                            const table =
                                                                                tableByRegionId[
                                                                                    r.id
                                                                                ] || null;
                                                                            const columns =
                                                                                table?.columns || [];
                                                                            const rows =
                                                                                table?.rows || [];
                                                                            const isLoading =
                                                                                !!loadingTableByRegionId[
                                                                                    r.id
                                                                                ];
                                                                            const err =
                                                                                tableErrorByRegionId[
                                                                                    r.id
                                                                                ] || null;

                                                                            if (!date || !date.trim()) {
                                                                                return (
                                                                                    <Table.Tbody>
                                                                                        <Table.Tr>
                                                                                            <Table.Td
                                                                                                className="py-4 text-slate-500"
                                                                                                colSpan={
                                                                                                    1 +
                                                                                                    (columns.length ||
                                                                                                        0) *
                                                                                                        2
                                                                                                }
                                                                                            >
                                                                                                Please select a date.
                                                                                            </Table.Td>
                                                                                        </Table.Tr>
                                                                                    </Table.Tbody>
                                                                                );
                                                                            }

                                                                            if (isLoading) {
                                                                                return (
                                                                                    <Table.Tbody>
                                                                                        <Table.Tr>
                                                                                            <Table.Td
                                                                                                className="py-4 text-slate-500"
                                                                                                colSpan={
                                                                                                    1 +
                                                                                                    (columns.length ||
                                                                                                        0) *
                                                                                                        2
                                                                                                }
                                                                                            >
                                                                                                Loading...
                                                                                            </Table.Td>
                                                                                        </Table.Tr>
                                                                                    </Table.Tbody>
                                                                                );
                                                                            }

                                                                            if (err) {
                                                                                return (
                                                                                    <Table.Tbody>
                                                                                        <Table.Tr>
                                                                                            <Table.Td
                                                                                                className="py-4 text-slate-500"
                                                                                                colSpan={
                                                                                                    1 +
                                                                                                    (columns.length ||
                                                                                                        0) *
                                                                                                        2
                                                                                                }
                                                                                            >
                                                                                                {err}
                                                                                            </Table.Td>
                                                                                        </Table.Tr>
                                                                                    </Table.Tbody>
                                                                                );
                                                                            }

                                                                            if (!table) {
                                                                                return (
                                                                                    <Table.Tbody>
                                                                                        <Table.Tr>
                                                                                            <Table.Td
                                                                                                className="py-4 text-slate-500"
                                                                                                colSpan={1}
                                                                                            >
                                                                                                No stock data loaded.
                                                                                            </Table.Td>
                                                                                        </Table.Tr>
                                                                                    </Table.Tbody>
                                                                                );
                                                                            }

                                                                            if (rows.length === 0) {
                                                                                return (
                                                                                    <Table.Tbody>
                                                                                        <Table.Tr>
                                                                                            <Table.Td
                                                                                                className="py-4 text-slate-500"
                                                                                                colSpan={1}
                                                                                            >
                                                                                                No outlets found for this region.
                                                                                            </Table.Td>
                                                                                        </Table.Tr>
                                                                                    </Table.Tbody>
                                                                                );
                                                                            }

                                                                            if (columns.length === 0) {
                                                                                return (
                                                                                    <Table.Tbody>
                                                                                        <Table.Tr>
                                                                                            <Table.Td
                                                                                                className="py-4 text-slate-500"
                                                                                                colSpan={1}
                                                                                            >
                                                                                                No own products found.
                                                                                            </Table.Td>
                                                                                        </Table.Tr>
                                                                                    </Table.Tbody>
                                                                                );
                                                                            }

                                                                            return (
                                                                                <>
                                                                                    <Table.Thead className="text-xs bg-white dark:bg-darkmode-600">
                                                                                        <Table.Tr>
                                                                                            <Table.Td
                                                                                                rowSpan={2}
                                                                                                className="py-2 font-medium bg-slate-50 dark:bg-darkmode-700 sticky left-0 z-30 border-r border-slate-200 dark:border-darkmode-400 min-w-[220px]"
                                                                                            >
                                                                                                Outlet
                                                                                            </Table.Td>
                                                                                            {columns.map((p) => (
                                                                                                <Table.Td
                                                                                                    key={`head_${r.id}_${p.productId}`}
                                                                                                    colSpan={2}
                                                                                                    className="py-2 px-2 font-medium text-center bg-slate-50/70 dark:bg-darkmode-700/30 min-w-[120px] max-w-[120px] whitespace-normal break-words leading-snug"
                                                                                                >
                                                                                                    {p.productName}
                                                                                                </Table.Td>
                                                                                            ))}
                                                                                        </Table.Tr>
                                                                                        <Table.Tr>
                                                                                            {columns.map((p) => (
                                                                                                <React.Fragment
                                                                                                    key={`sub_${r.id}_${p.productId}`}
                                                                                                >
                                                                                                    <Table.Td className="py-2 px-2 font-medium text-center bg-success/10 dark:bg-success/10 whitespace-nowrap min-w-[60px] w-[60px]">
                                                                                                        Opening
                                                                                                    </Table.Td>
                                                                                                    <Table.Td className="py-2 px-2 font-medium text-center bg-warning/10 dark:bg-warning/10 whitespace-nowrap min-w-[60px] w-[60px]">
                                                                                                        Closing
                                                                                                    </Table.Td>
                                                                                                </React.Fragment>
                                                                                            ))}
                                                                                        </Table.Tr>
                                                                                    </Table.Thead>
                                                                                    <Table.Tbody>
                                                                                        {rows.map((o) => (
                                                                                            <Table.Tr
                                                                                                key={`out_${r.id}_${o.outletId}`}
                                                                                                className="[&_td]:border-b [&_td]:border-slate-200/60"
                                                                                            >
                                                                                                <Table.Td className="py-2 bg-slate-50 dark:bg-darkmode-700 whitespace-nowrap sticky left-0 z-20 border-r border-slate-200 dark:border-darkmode-400 min-w-[220px] max-w-[280px] truncate">
                                                                                                    {o.outletName}
                                                                                                </Table.Td>
                                                                                                {columns.map((p, idx) => (
                                                                                                    <React.Fragment
                                                                                                        key={`cell_${o.outletId}_${p.productId}`}
                                                                                                    >
                                                                                                        <Table.Td
                                                                                                            className={[
                                                                                                                "py-2 px-2 text-center tabular-nums min-w-[60px] w-[60px]",
                                                                                                                o.openingsBelowThreshold?.[
                                                                                                                    idx
                                                                                                                ]
                                                                                                                    ? "bg-danger/15 dark:bg-danger/20 text-danger font-medium"
                                                                                                                    : "bg-success/10 dark:bg-success/10",
                                                                                                            ].join(
                                                                                                                " "
                                                                                                            )}
                                                                                                        >
                                                                                                            {formatIntCell(
                                                                                                                o.openings?.[
                                                                                                                    idx
                                                                                                                ] ??
                                                                                                                    null
                                                                                                            )}
                                                                                                        </Table.Td>
                                                                                                        <Table.Td
                                                                                                            className={[
                                                                                                                "py-2 px-2 text-center tabular-nums min-w-[60px] w-[60px]",
                                                                                                                o.closingsBelowThreshold?.[
                                                                                                                    idx
                                                                                                                ]
                                                                                                                    ? "bg-danger/15 dark:bg-danger/20 text-danger font-medium"
                                                                                                                    : "bg-warning/10 dark:bg-warning/10",
                                                                                                            ].join(
                                                                                                                " "
                                                                                                            )}
                                                                                                        >
                                                                                                            {formatIntCell(
                                                                                                                o.closings?.[
                                                                                                                    idx
                                                                                                                ] ??
                                                                                                                    null
                                                                                                            )}
                                                                                                        </Table.Td>
                                                                                                    </React.Fragment>
                                                                                                ))}
                                                                                            </Table.Tr>
                                                                                        ))}
                                                                                    </Table.Tbody>
                                                                                </>
                                                                            );
                                                                        })()}
                                                                    </Table>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                )}

                                {/* Stock By: Class (containers are OutletCategory) */}
                                {stockBy === "Class" && (
                                    <div className="mt-6 grid grid-cols-12 gap-x-6 gap-y-6">
                                        {loadingClasses ? (
                                            <div className="col-span-12 flex items-center justify-center py-10">
                                                <div className="text-center">
                                                    <Lucide
                                                        icon="Loader2"
                                                        className="w-7 h-7 animate-spin mx-auto mb-2"
                                                    />
                                                    <div>Loading classes...</div>
                                                </div>
                                            </div>
                                        ) : classes.length === 0 ? (
                                            <div className="col-span-12 text-slate-500">
                                                No outlet categories found.
                                            </div>
                                        ) : (
                                            classes.map((c) => (
                                                <div key={c.id} className="col-span-12">
                                                    <div className="p-5 box">
                                                        <div className="flex items-center gap-3">
                                                            <div className="text-base font-medium">
                                                                {c.catName}
                                                            </div>
                                                            <button
                                                                type="button"
                                                                className="ml-auto inline-flex items-center text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                                                                onClick={() => toggleClass(c.id)}
                                                                aria-expanded={expandedClassIds.has(c.id)}
                                                                aria-controls={`class-${c.id}`}
                                                            >
                                                                <Lucide
                                                                    icon={
                                                                        expandedClassIds.has(c.id)
                                                                            ? "ChevronUp"
                                                                            : "ChevronDown"
                                                                    }
                                                                    className="w-5 h-5 stroke-[1.3]"
                                                                />
                                                            </button>
                                                        </div>

                                                        {expandedClassIds.has(c.id) && (
                                                            <div
                                                                id={`class-${c.id}`}
                                                                className="mt-4"
                                                            >
                                                                <div className="overflow-auto">
                                                                    <Table className="border-b border-slate-200/60 text-xs">
                                                                        {(() => {
                                                                            const table =
                                                                                tableByClassId[
                                                                                    c.id
                                                                                ] || null;
                                                                            const columns =
                                                                                table?.columns || [];
                                                                            const rows =
                                                                                table?.rows || [];
                                                                            const isLoading =
                                                                                !!loadingTableByClassId[
                                                                                    c.id
                                                                                ];
                                                                            const err =
                                                                                tableErrorByClassId[
                                                                                    c.id
                                                                                ] || null;

                                                                            if (!date || !date.trim()) {
                                                                                return (
                                                                                    <Table.Tbody>
                                                                                        <Table.Tr>
                                                                                            <Table.Td
                                                                                                className="py-4 text-slate-500"
                                                                                                colSpan={
                                                                                                    1 +
                                                                                                    (columns.length ||
                                                                                                        0) *
                                                                                                        2
                                                                                                }
                                                                                            >
                                                                                                Please select a date.
                                                                                            </Table.Td>
                                                                                        </Table.Tr>
                                                                                    </Table.Tbody>
                                                                                );
                                                                            }

                                                                            if (isLoading) {
                                                                                return (
                                                                                    <Table.Tbody>
                                                                                        <Table.Tr>
                                                                                            <Table.Td
                                                                                                className="py-4 text-slate-500"
                                                                                                colSpan={
                                                                                                    1 +
                                                                                                    (columns.length ||
                                                                                                        0) *
                                                                                                        2
                                                                                                }
                                                                                            >
                                                                                                Loading...
                                                                                            </Table.Td>
                                                                                        </Table.Tr>
                                                                                    </Table.Tbody>
                                                                                );
                                                                            }

                                                                            if (err) {
                                                                                return (
                                                                                    <Table.Tbody>
                                                                                        <Table.Tr>
                                                                                            <Table.Td
                                                                                                className="py-4 text-slate-500"
                                                                                                colSpan={
                                                                                                    1 +
                                                                                                    (columns.length ||
                                                                                                        0) *
                                                                                                        2
                                                                                                }
                                                                                            >
                                                                                                {err}
                                                                                            </Table.Td>
                                                                                        </Table.Tr>
                                                                                    </Table.Tbody>
                                                                                );
                                                                            }

                                                                            if (!table) {
                                                                                return (
                                                                                    <Table.Tbody>
                                                                                        <Table.Tr>
                                                                                            <Table.Td
                                                                                                className="py-4 text-slate-500"
                                                                                                colSpan={1}
                                                                                            >
                                                                                                No stock data loaded.
                                                                                            </Table.Td>
                                                                                        </Table.Tr>
                                                                                    </Table.Tbody>
                                                                                );
                                                                            }

                                                                            if (rows.length === 0) {
                                                                                return (
                                                                                    <Table.Tbody>
                                                                                        <Table.Tr>
                                                                                            <Table.Td
                                                                                                className="py-4 text-slate-500"
                                                                                                colSpan={1}
                                                                                            >
                                                                                                No outlets found for this class.
                                                                                            </Table.Td>
                                                                                        </Table.Tr>
                                                                                    </Table.Tbody>
                                                                                );
                                                                            }

                                                                            if (columns.length === 0) {
                                                                                return (
                                                                                    <Table.Tbody>
                                                                                        <Table.Tr>
                                                                                            <Table.Td
                                                                                                className="py-4 text-slate-500"
                                                                                                colSpan={1}
                                                                                            >
                                                                                                No own products found.
                                                                                            </Table.Td>
                                                                                        </Table.Tr>
                                                                                    </Table.Tbody>
                                                                                );
                                                                            }

                                                                            return (
                                                                                <>
                                                                                    <Table.Thead className="text-xs bg-white dark:bg-darkmode-600">
                                                                                        <Table.Tr>
                                                                                            <Table.Td
                                                                                                rowSpan={2}
                                                                                                className="py-2 font-medium bg-slate-50 dark:bg-darkmode-700 sticky left-0 z-30 border-r border-slate-200 dark:border-darkmode-400 min-w-[220px]"
                                                                                            >
                                                                                                Outlet
                                                                                            </Table.Td>
                                                                                            {columns.map((p) => (
                                                                                                <Table.Td
                                                                                                    key={`head_${c.id}_${p.productId}`}
                                                                                                    colSpan={2}
                                                                                                    className="py-2 px-2 font-medium text-center bg-slate-50/70 dark:bg-darkmode-700/30 min-w-[120px] max-w-[120px] whitespace-normal break-words leading-snug"
                                                                                                >
                                                                                                    {p.productName}
                                                                                                </Table.Td>
                                                                                            ))}
                                                                                        </Table.Tr>
                                                                                        <Table.Tr>
                                                                                            {columns.map((p) => (
                                                                                                <React.Fragment
                                                                                                    key={`sub_${c.id}_${p.productId}`}
                                                                                                >
                                                                                                    <Table.Td className="py-2 px-2 font-medium text-center bg-success/10 dark:bg-success/10 whitespace-nowrap min-w-[60px] w-[60px]">
                                                                                                        Opening
                                                                                                    </Table.Td>
                                                                                                    <Table.Td className="py-2 px-2 font-medium text-center bg-warning/10 dark:bg-warning/10 whitespace-nowrap min-w-[60px] w-[60px]">
                                                                                                        Closing
                                                                                                    </Table.Td>
                                                                                                </React.Fragment>
                                                                                            ))}
                                                                                        </Table.Tr>
                                                                                    </Table.Thead>
                                                                                    <Table.Tbody>
                                                                                        {rows.map((o) => (
                                                                                            <Table.Tr
                                                                                                key={`out_${c.id}_${o.outletId}`}
                                                                                                className="[&_td]:border-b [&_td]:border-slate-200/60"
                                                                                            >
                                                                                                <Table.Td className="py-2 bg-slate-50 dark:bg-darkmode-700 whitespace-nowrap sticky left-0 z-20 border-r border-slate-200 dark:border-darkmode-400 min-w-[220px] max-w-[280px] truncate">
                                                                                                    {o.outletName}
                                                                                                </Table.Td>
                                                                                                {columns.map((p, idx) => (
                                                                                                    <React.Fragment
                                                                                                        key={`cell_${o.outletId}_${p.productId}`}
                                                                                                    >
                                                                                                        <Table.Td
                                                                                                            className={[
                                                                                                                "py-2 px-2 text-center tabular-nums min-w-[60px] w-[60px]",
                                                                                                                o.openingsBelowThreshold?.[
                                                                                                                    idx
                                                                                                                ]
                                                                                                                    ? "bg-danger/15 dark:bg-danger/20 text-danger font-medium"
                                                                                                                    : "bg-success/10 dark:bg-success/10",
                                                                                                            ].join(
                                                                                                                " "
                                                                                                            )}
                                                                                                        >
                                                                                                            {formatIntCell(
                                                                                                                o.openings?.[
                                                                                                                    idx
                                                                                                                ] ??
                                                                                                                    null
                                                                                                            )}
                                                                                                        </Table.Td>
                                                                                                        <Table.Td
                                                                                                            className={[
                                                                                                                "py-2 px-2 text-center tabular-nums min-w-[60px] w-[60px]",
                                                                                                                o.closingsBelowThreshold?.[
                                                                                                                    idx
                                                                                                                ]
                                                                                                                    ? "bg-danger/15 dark:bg-danger/20 text-danger font-medium"
                                                                                                                    : "bg-warning/10 dark:bg-warning/10",
                                                                                                            ].join(
                                                                                                                " "
                                                                                                            )}
                                                                                                        >
                                                                                                            {formatIntCell(
                                                                                                                o.closings?.[
                                                                                                                    idx
                                                                                                                ] ??
                                                                                                                    null
                                                                                                            )}
                                                                                                        </Table.Td>
                                                                                                    </React.Fragment>
                                                                                                ))}
                                                                                            </Table.Tr>
                                                                                        ))}
                                                                                    </Table.Tbody>
                                                                                </>
                                                                            );
                                                                        })()}
                                                                    </Table>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                )}

                                {/* Other Stock By modes (placeholder for now) */}
                                {/* (no placeholder needed now for Class) */}
                            </div>
                        </div>
                    </div>
                </div>
            </RoleGate>
        </ProtectedRoute>
    );
}


